import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Package, Truck, RefreshCcw, Save } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { adminAPI } from '../../lib/api';
import { formatPrice, formatDateTime, ORDER_STATUS } from '../../lib/utils';
import { toast } from 'sonner';

export default function OrderDetailPage() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [trackingNumber, setTrackingNumber] = useState('');
  const [trackingUrl, setTrackingUrl] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [status, setStatus] = useState('');
  const [refundDialogOpen, setRefundDialogOpen] = useState(false);

  useEffect(() => {
    loadOrder();
  }, [orderId]);

  const loadOrder = async () => {
    try {
      const response = await adminAPI.getOrder(orderId);
      setOrder(response.data);
      setTrackingNumber(response.data.tracking_number || '');
      setTrackingUrl(response.data.tracking_url || '');
      setAdminNotes(response.data.admin_notes || '');
      setStatus(response.data.status);
    } catch (error) {
      console.error('Error loading order:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const updates = {
        status,
        tracking_number: trackingNumber,
        tracking_url: trackingUrl,
        admin_notes: adminNotes
      };
      await adminAPI.updateOrder(orderId, updates);
      toast.success('Commande mise à jour');
      loadOrder();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  const handleAssignToMe = async () => {
    setSaving(true);
    try {
      await adminAPI.assignOrderToMe(orderId);
      toast.success('Commande assignée');
      loadOrder();
    } catch (error) {
      toast.error('Erreur lors de l\'assignation');
    } finally {
      setSaving(false);
    }
  };

  const handleRefund = async () => {
    try {
      await adminAPI.refundOrder(orderId);
      toast.success('Remboursement effectué');
      loadOrder();
    } catch (error) {
      toast.error('Erreur lors du remboursement');
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-48 bg-secondary animate-pulse rounded" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <div className="h-64 bg-secondary animate-pulse rounded-lg" />
            <div className="h-48 bg-secondary animate-pulse rounded-lg" />
          </div>
          <div className="h-96 bg-secondary animate-pulse rounded-lg" />
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground mb-4">Commande introuvable</p>
        <Button onClick={() => navigate('/admin/orders')}>Retour aux commandes</Button>
      </div>
    );
  }

  const statusInfo = ORDER_STATUS[order.status] || ORDER_STATUS.PENDING_PAYMENT;
  const canRefund = ['PAID', 'SHIPPED'].includes(order.status);

  return (
    <div className="space-y-6" data-testid="order-detail">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/admin/orders')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold font-mono">{order.order_number}</h1>
            <p className="text-muted-foreground">{formatDateTime(order.created_at)}</p>
          </div>
        </div>
        <Badge className={statusInfo.color} data-testid="order-status">
          {statusInfo.label}
        </Badge>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Items */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="h-5 w-5" />
                Articles ({order.items?.length || 0})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {order.items?.map((item) => (
                <div key={item.id} className="flex gap-4 p-3 bg-secondary/50 rounded-lg">
                  <div className="w-16 h-20 bg-secondary overflow-hidden flex-shrink-0 rounded">
                    {item.product_image && (
                      <img
                        src={item.product_image}
                        alt={item.product_name}
                        className="w-full h-full object-cover"
                      />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium">{item.product_name}</p>
                    <p className="text-sm text-muted-foreground">Qté: {item.quantity}</p>
                  </div>
                  <p className="font-mono font-medium">
                    {formatPrice(item.price_cents * item.quantity)}
                  </p>
                </div>
              ))}

              {/* Totals */}
              <div className="border-t border-border pt-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Sous-total</span>
                  <span className="font-mono">{formatPrice(order.subtotal_cents)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Livraison</span>
                  <span className="font-mono">
                    {order.shipping_cents === 0 ? 'Gratuite' : formatPrice(order.shipping_cents)}
                  </span>
                </div>
                {order.discount_cents > 0 && (
                  <div className="flex justify-between text-sm text-green-500">
                    <span>Réduction {order.promo_code && `(${order.promo_code})`}</span>
                    <span className="font-mono">-{formatPrice(order.discount_cents)}</span>
                  </div>
                )}
                <div className="flex justify-between text-lg font-bold pt-2 border-t border-border">
                  <span>Total</span>
                  <span className="font-mono">{formatPrice(order.total_cents)}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Shipping & Tracking */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Truck className="h-5 w-5" />
                Expédition
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="trackingNumber">Numéro de suivi</Label>
                  <Input
                    id="trackingNumber"
                    value={trackingNumber}
                    onChange={(e) => setTrackingNumber(e.target.value)}
                    placeholder="Ex: 1Z999AA10123456784"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="trackingUrl">URL de suivi</Label>
                  <Input
                    id="trackingUrl"
                    value={trackingUrl}
                    onChange={(e) => setTrackingUrl(e.target.value)}
                    placeholder="https://..."
                    className="mt-1"
                  />
                </div>
              </div>

              <div>
                <Label>Adresse de livraison</Label>
                <div className="mt-1 p-3 bg-secondary/50 rounded-lg text-sm">
                  <p className="font-medium">
                    {order.shipping_address?.first_name} {order.shipping_address?.last_name}
                  </p>
                  <p>{order.shipping_address?.address_line1}</p>
                  {order.shipping_address?.address_line2 && (
                    <p>{order.shipping_address.address_line2}</p>
                  )}
                  <p>
                    {order.shipping_address?.postal_code} {order.shipping_address?.city}
                  </p>
                  <p>{order.shipping_address?.country}</p>
                  {order.shipping_address?.phone && (
                    <p className="mt-1">Tél: {order.shipping_address.phone}</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Actions */}
          <Card>
            <CardHeader>
              <CardTitle>Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="status">Statut</Label>
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(ORDER_STATUS).map(([key, { label }]) => (
                      <SelectItem key={key} value={key}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="adminNotes">Notes internes</Label>
                <Textarea
                  id="adminNotes"
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  placeholder="Notes visibles uniquement par l'équipe..."
                  className="mt-1"
                  rows={3}
                />
              </div>

              <Button 
                className="w-full" 
                onClick={handleSave}
                disabled={saving}
                data-testid="save-order"
              >
                <Save className="h-4 w-4 mr-2" />
                {saving ? 'Enregistrement...' : 'Enregistrer'}
              </Button>

              <Button 
                variant="secondary"
                className="w-full"
                onClick={handleAssignToMe}
                disabled={saving}
              >
                M'assigner
              </Button>

              {canRefund && (
                <Button 
                  variant="destructive" 
                  className="w-full"
                  onClick={() => setRefundDialogOpen(true)}
                >
                  <RefreshCcw className="h-4 w-4 mr-2" />
                  Rembourser
                </Button>
              )}

              <AlertDialog open={refundDialogOpen} onOpenChange={setRefundDialogOpen}>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Confirmer le remboursement</AlertDialogTitle>
                    <AlertDialogDescription>
                      Cette action va rembourser la commande {order?.order_number} 
                      d'un montant de {formatPrice(order?.total_cents || 0)}.
                      Cette action est irréversible.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Annuler</AlertDialogCancel>
                    <AlertDialogAction onClick={() => {
                      setRefundDialogOpen(false);
                      handleRefund();
                    }}>
                      Confirmer le remboursement
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </CardContent>
          </Card>

          {/* Customer */}
          <Card>
            <CardHeader>
              <CardTitle>Client</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <p className="text-sm text-muted-foreground">Email</p>
                <p className="font-medium">{order.email}</p>
              </div>
              {order.user_id && (
                <div>
                  <p className="text-sm text-muted-foreground">ID Client</p>
                  <p className="font-mono text-sm">{order.user_id}</p>
                </div>
              )}
              {order.assigned_to && (
                <div>
                  <p className="text-sm text-muted-foreground">Assigné à</p>
                  <p className="font-medium">{order.assigned_to_name || order.assigned_to}</p>
                </div>
              )}
              {order.notes && (
                <div>
                  <p className="text-sm text-muted-foreground">Note du client</p>
                  <p className="text-sm">{order.notes}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Timeline */}
          <Card>
            <CardHeader>
              <CardTitle>Historique</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Créée</span>
                  <span>{formatDateTime(order.created_at)}</span>
                </div>
                {order.paid_at && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Payée</span>
                    <span>{formatDateTime(order.paid_at)}</span>
                  </div>
                )}
                {order.shipped_at && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Expédiée</span>
                    <span>{formatDateTime(order.shipped_at)}</span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
